import { z } from "zod";
import { IsoDateTimeSchema } from "./primitives.js";
// ADR-032 (live catalog): "prelaunch" was the free pre-launch month (€0, no
// card, expires after 30 days, never renews). Retired from new offers by
// item 8 (2026-09-12) — kept in this enum ONLY so an existing prelaunch
// subscriber's `GET /v1/billing/subscription` can still report their real
// plan key; `GET /v1/billing/offers` never serves it anymore.
// "freeClaim" (item 8) is its replacement: the "BiteBalance 2-Week Free
// Plan" — €0, no card (`accepted_payment_methods: ["free"]` on Whop's own
// plan), expires after 14 days, never renews. A card-free trial that later
// auto-converts to a paid plan does not exist on Whop (confirmed against
// Whop's own docs) — access after expiry requires an explicit, immediate-
// billing subscribe to monthly/quarterly/annual.
// "free" is retained for wire/DB compatibility with the retired sandbox
// plan but is no longer offered. "monthly"/"quarterly"/"annual" are the
// ADR-007 canonical paid offers.
export const BillingPlanKeySchema = z.enum(["free", "prelaunch", "freeClaim", "monthly", "quarterly", "annual"]);
export const BillingOfferSchema = z.object({
    planKey: BillingPlanKeySchema,
    priceEurCents: z.number().int(),
    /**
     * "once" (2026-09-13, independent-review finding): a one-time,
     * non-renewing plan with a fixed access window that BiteBalance itself
     * expires (`subscriptions.expires_at` — see `shared/billingPlans.ts`'s
     * `EXPIRING_DB_PLAN_KEYS`) — `freeClaim` and `prelaunch`, never a
     * recurring `month`/`quarter`/`year` plan. Both clients special-case
     * these plan keys for display (`isFreeTierPlan`) before ever reading
     * `billingPeriod`, so this was previously mislabeled `"month"` with no
     * visible symptom — fixed here so the field itself is never wrong, per
     * this repo's "never invent billing data" rule.
     */
    billingPeriod: z.enum(["forever", "once", "month", "quarter", "year"]),
    trialDays: z.number().int().nonnegative(),
    renewalMessageKey: z.string(),
    /** False when the server has no sellable checkout link for this offer yet — never a fabricated one. */
    available: z.boolean(),
    /** Hosted checkout link, or null when `available` is false. */
    checkoutUrl: z.string().url().nullable(),
    /**
     * ADR-085. Present only for iOS callers while `apple_iap_enabled` is on: the
     * App Store product id to purchase through StoreKit. Prices shown to the user
     * come from StoreKit, never from `priceEurCents`, whenever this field is
     * present. Absent = today's Whop checkout contract (byte-identical response).
     */
    appleProductId: z.string().optional(),
});
/** ADR-085: Apple's server environment of the signed data behind a subscription row. */
export const AppleEnvironmentSchema = z.enum(["Production", "Sandbox"]);
/** ADR-085: how an offers response expects the purchase to happen; absent = the Whop hosted checkout. */
export const BillingPurchaseChannelSchema = z.enum(["whop_checkout", "apple_iap"]);
// GET /v1/billing/offers response
export const BillingOffersResponseSchema = z.object({
    offers: z.array(BillingOfferSchema),
    /** True when the caller holds an active `pilot` entitlement — UI copy hangs "Pilot access — no charge" on this. */
    pilotAccess: z.boolean(),
    /**
     * ADR-085. Present only when the server switched this response to Apple
     * In-App Purchase (iOS caller + `apple_iap_enabled` on). Absent = today's
     * Whop checkout contract.
     */
    purchaseChannel: BillingPurchaseChannelSchema.optional(),
    /**
     * ADR-085 phase 3. Present only when `purchaseChannel` is `apple_iap` and no
     * Apple purchase may be offered: the caller holds a live paid Whop membership
     * (never bill twice).
     */
    applePurchaseBlockedReason: z.enum(["live_whop_subscription"]).optional(),
    /**
     * ADR-085 phase 2. Present ONLY while the Whop checkout is retired (`whop_checkout_retired`) for a caller that is not in the Apple
     * channel (web; a non-iOS device): nothing in this response can be bought here — every offer is `available:false`, `checkoutUrl:null`
     * and carries no `appleProductId`. Purchases happen in the iPhone app. The free period is claimed through `POST /v1/billing/free-access`
     * while `freePeriodClaimable`. Absent = today's contract (Whop checkout, or the Apple channel).
     * Deliberately NOT a new `purchaseChannel` value: the closed enum would make an older client bundle fail to parse the whole response.
     */
    appStoreOnly: z.object({ freePeriodClaimable: z.boolean() }).optional(),
});
/** GET /v1/billing/offers query (ADR-085): iOS callers pass `platform=ios`; omitted/`web` keeps today's contract. */
export const BillingOffersQuerySchema = z.object({ platform: z.enum(["web", "ios"]).optional() });
// POST /v1/billing/checkout request — explicit plan selection required.
export const BillingCheckoutRequestSchema = z.object({
    planKey: BillingPlanKeySchema,
    /**
     * Which client is asking (2026-09-14): decides where Whop's hosted
     * checkout redirects when it finishes. Defaults to `"web"` when omitted
     * — a real web browser can never open a `bitebalance://` deep link, so
     * unspecified/misidentified callers must fail safe toward the web
     * redirect, never the other way around. Mobile explicitly passes
     * `"mobile"` to get sent back into the app instead of the marketing
     * site (previously: every platform redirected to the website, which for
     * mobile meant a stray in-app-browser tab showing the site's own
     * sign-in prompt — that browser context has no web session at all,
     * confusing but harmless since the app's own poll already detects the
     * grant independently; the deep-link redirect closes that browser tab
     * before it ever renders anything, rather than racing to auto-dismiss
     * it after the fact).
     */
    platform: z.enum(["web", "mobile"]).optional(),
});
export const BillingCheckoutResponseSchema = z.object({
    checkoutUrl: z.string().url(),
    expiresAt: IsoDateTimeSchema,
});
/**
 * POST /v1/billing/cancel — in-app cancellation (ADR-032c): the server
 * cancels the caller's active Whop membership AT PERIOD END via the admin
 * API, so users never need a Whop login to cancel. The entitlement itself
 * still changes only via the signed webhook.
 */
export const BillingCancelRequestSchema = z.object({});
export const BillingCancelResponseSchema = z.object({
    canceled: z.literal(true),
    /** ISO timestamp access runs until, when the provider reported it — never invented. */
    accessUntil: IsoDateTimeSchema.nullable(),
});
/**
 * GET /v1/billing/subscription — the caller's current subscription with the
 * provider-fresh detail the Plan & billing panel renders (trial end,
 * renewal date, scheduled cancellation). Provider fields are null when the
 * live lookup fails — the panel then degrades to the DB-known basics,
 * never an invented date.
 */
export const BillingSubscriptionResponseSchema = z.object({
    planKey: BillingPlanKeySchema,
    state: z.enum(["active", "trialing", "past_due", "canceled", "expired"]),
    /** Trial end (while trialing) or next renewal / access boundary. */
    currentPeriodEnd: IsoDateTimeSchema.nullable(),
    cancelAtPeriodEnd: z.boolean(),
    /**
     * ADR-085. Which system bills this subscription; absent for today's Whop
     * rows so the existing payload bytes stay unchanged.
     */
    provider: z.enum(["apple", "bitebalance"]).optional(),
    /** ADR-085. Apple's server environment for an `apple` row; absent otherwise. */
    environment: AppleEnvironmentSchema.optional(),
});
/**
 * POST /v1/billing/apple/transactions (ADR-085) — the app submits the signed
 * StoreKit 2 transaction right after a purchase or a restore. The server
 * verifies Apple's JWS chain itself (never trusts the client's claim), rejects
 * a transaction whose `appAccountToken` is not the authenticated user, and
 * applies it through the same single writer the App Store Server
 * Notifications use.
 */
export const BillingAppleTransactionRequestSchema = z.object({
    /** StoreKit 2 `Transaction.jwsRepresentation` of the purchased/restored transaction. */
    signedTransaction: z.string().min(1),
    /** Optional `RenewalInfo.jwsRepresentation` from `Product.SubscriptionInfo.Status` — lets the server record cancel-at-period-end immediately. */
    signedRenewalInfo: z.string().min(1).optional(),
});
export const BillingAppleTransactionResponseSchema = z.object({
    outcome: z.enum(["applied", "duplicate", "stale", "sandbox_not_granted"]),
    planKey: BillingPlanKeySchema,
    state: z.enum(["active", "trialing", "past_due", "canceled", "expired"]),
    currentPeriodEnd: IsoDateTimeSchema.nullable(),
    cancelAtPeriodEnd: z.boolean(),
    environment: AppleEnvironmentSchema,
    /** False when the verified transaction did not grant access (sandbox while sandbox grants are off, or a revoked/expired transaction). */
    accessGranted: z.boolean(),
});
/**
 * POST /v1/billing/free-access (ADR-085) — the server-side 14-day free period
 * (provider `bitebalance`, plan key `individual_free_claim`), granted at most
 * once per account across providers.
 */
export const BillingFreeAccessRequestSchema = z.object({});
export const BillingFreeAccessResponseSchema = z.object({
    granted: z.literal(true),
    expiresAt: IsoDateTimeSchema,
});
//# sourceMappingURL=billing.js.map